import React from 'react';
import PropTypes from 'prop-types';
import {
  Textarea,
  Textfield,
  HiddenField,
  DropDown,
  Checkbox,
  RadioButton,
  SingleDate,
  DateRange,
  EntryPicker,
  CountrySelect,
} from '../../components/fields';
import MarkdownRenderer from 'zengenti-forms-package/utils/markdown-to-jsx';

const FormComposer = ({
  fields,
  formData,
  formId,
  setValue,
  setDateRangeValues,
  onValidateField,
  defaultLanguage,
  errors,
  useDefaultTheme,
  entries,
}) => {

  if (!fields || fields.length < 1) return null;
  return fields.map((field, idx) => {
    if (!field) return null;
    switch (field.type) {
      case 'number':
      case 'textfield': {
        const instructions = field?.editor?.instructions?.[defaultLanguage];
        return (
          <Textfield
            key={`${field.id}-${idx}`}
            field={field}
            type={field.id === 'password' ? 'password' : field.type}
            id={field.id}
            label={field.name && field.name[defaultLanguage]}
            formId={formId}
            setValue={setValue}
            validations={field.validations}
            onValidateField={onValidateField}
            defaultLanguage={defaultLanguage}
            defaultValue={(formData && formData[field.id]) || field.default}
            placeholder={field.editor}
            errors={errors}
            useDefaultTheme={useDefaultTheme}
            instructions={instructions}
          />
        );
      }
      case 'textarea': {
        return (
          <Textarea
            key={`${field.id}-${idx}`}
            field={field}
            type={field.type}
            id={field.id}
            label={field.name && field.name[defaultLanguage]}
            formId={formId}
            setValue={setValue}
            validations={field.validations}
            defaultLanguage={defaultLanguage}
            defaultValue={field.default}
            placeholder={field.editor}
            onValidateField={onValidateField}
            useDefaultTheme={useDefaultTheme}
            errors={errors}
          />
        );
      }
      case 'dropdown': {
        return (
          <DropDown
            key={`${field.id}-${idx}`}
            id={field.id}
            label={field.name && field.name[defaultLanguage]}
            validations={field.validations}
            defaultLanguage={defaultLanguage}
            defaultValue={field.default}
            formId={formId}
            setValue={setValue}
            useDefaultTheme={useDefaultTheme}
          />
        );
      }
      case 'checkbox': {
        return (
          <Checkbox
            key={`${field.id}-${idx}`}
            id={field.id}
            name={field.name}
            label={field.name && field.name[defaultLanguage]}
            validations={field.validations}
            defaultLanguage={defaultLanguage}
            setValue={setValue}
            formId={formId}
            defaultValue={(formData && formData[field.id]) || field.default}
            type={field.dataType === 'boolean' ? 'single' : 'multiple'}
            useDefaultTheme={useDefaultTheme}
          />
        );
      }
      case 'radio': {
        return (
          <RadioButton
            key={`${field.id}-${idx}`}
            id={field.id}
            type={field.type}
            formId={formId}
            setValue={setValue}
            label={field.name && field.name[defaultLanguage]}
            validations={field.validations}
            defaultLanguage={defaultLanguage}
            useDefaultTheme={useDefaultTheme}
          />
        );
      }
      case 'entryPicker': {
        const type = field.dataType === 'objectArray' ? 'checkbox' : 'radio';
        const results = entries && entries[field.id] && entries[field.id].items;
        return (
          <EntryPicker
            key={`${field.id}-${idx}`}
            type={type}
            results={results}
            id={field.id}
            label={field.name && field.name[defaultLanguage]}
            useDefaultTheme={useDefaultTheme}
            setValue={setValue}
            onValidateField={onValidateField}
            formId={formId}
          />
        );
      }
      case 'date': {
        return (
          <SingleDate
            key={`${field.id}-${idx}`}
            type={field.type}
            id={field.id}
            label={field.name && field.name[defaultLanguage]}
            validations={field.validations}
            formId={formId}
            setValue={setValue}
            useDefaultTheme={useDefaultTheme}
          />
        );
      }
      case 'dateRange': {
        return (
          <DateRange
            key={`${field.id}-${idx}`}
            id={field.id}
            label={field.name && field.name[defaultLanguage]}
            validations={field.validations}
            formId={formId}
            setDateRangeValues={setDateRangeValues}
            useDefaultTheme={useDefaultTheme}
          />
        );
      }
      case 'hidden': {
        return (
          <HiddenField
            key={`${field.id}-${idx}`}
            type={field.type}
            id={field.id}
            label={field.name && field.name[defaultLanguage]}
            defaultLanguage={defaultLanguage}
            defaultValue={field.default}
            placeholder={field.editor}
          />
        );
      }
      case 'country': {
        return (
          <CountrySelect
            key={`${field.id}-${idx}`}
            formId={formId}
            field={field}
            id={field.id}
            label={field.name && field.name[defaultLanguage]}
            validations={field.validations}
            defaultValue={(formData && formData[field.id]) || field?.default?.[defaultLanguage]}
            placeholder={field?.editor?.properties?.placeholderText?.[defaultLanguage]}
          />
        )
      }
      case 'content': {
        const { name, editor } = field || {};
        const instructions = editor?.instructions?.[defaultLanguage];

        return (
          <span className="form__content" data-form="title" style={{ display: 'block' }} key={`${field.id}-${idx}`}>
            <span className="form__content--title">{name?.[defaultLanguage]}</span>
            {instructions && <MarkdownRenderer className="form__content--markdown" markdown={instructions} />}
          </span>
        );
      }
    }
    
  });
};

FormComposer.propTypes = {
  fields: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  formData: PropTypes.object,
  entries: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  formId: PropTypes.string,
  setValue: PropTypes.func,
  onValidateField: PropTypes.func,
  setDateRangeValues: PropTypes.func,
  defaultLanguage: PropTypes.string,
  errors: PropTypes.array,
  useDefaultTheme: PropTypes.bool,
};

export default FormComposer;
